import { useState } from 'react'
import { StarRatingInput } from './StarRating'

const COMMENT_MIN = 20
const COMMENT_MAX = 1000
const TITLE_MAX = 255

/**
 * Write-a-review modal. Create only — the Review APIs (per the Postman
 * collection) expose Create/List/Summary, not Update/Delete, so there is no
 * edit flow to build against a real endpoint.
 */
export default function ReviewForm({ astrologerName, onSubmit, onClose }) {
  const [rating, setRating] = useState(0)
  const [title, setTitle] = useState('')
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState(null)
  const [formError, setFormError] = useState(null)

  const commentTooShort = comment.trim().length > 0 && comment.trim().length < COMMENT_MIN
  const canSubmit = rating > 0 && comment.trim().length >= COMMENT_MIN && comment.length <= COMMENT_MAX && !submitting

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setFieldErrors(null)
    setFormError(null)
    try {
      await onSubmit({ rating, comment: comment.trim(), title: title.trim() || undefined })
    } catch (err) {
      const details = err?.error?.details
      if (details && typeof details === 'object') {
        setFieldErrors(details)
      }
      setFormError(err?.error?.message || err?.message || 'Could not submit your review. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center px-4 py-8 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-lg rounded-2xl overflow-hidden bg-white border border-[#D7C29A]/30 shadow-2xl my-auto">
        <div className="h-[3px] w-full bg-gradient-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-cinzel text-xl text-[#1C140A]">Write a review</h3>
              <p className="text-xs text-[#8B7355] mt-1">Share your experience with {astrologerName || 'this astrologer'}.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[#8B7355] hover:bg-stone-100 transition-colors disabled:opacity-40"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-2">Your rating</p>
              <StarRatingInput value={rating} onChange={setRating} disabled={submitting} />
              {fieldErrors?.rating && <p className="text-xs text-red-600 mt-1">{fieldErrors.rating[0]}</p>}
            </div>

            <div>
              <label htmlFor="review-title" className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-2 block">
                Title <span className="normal-case font-normal text-[#8B7355]/70">(optional)</span>
              </label>
              <input
                id="review-title"
                type="text"
                value={title}
                maxLength={TITLE_MAX}
                disabled={submitting}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Sum up your experience"
                className="w-full rounded-lg border border-[#D7C29A]/40 px-3.5 py-2.5 text-sm text-[#1C140A] focus:outline-none focus:ring-2 focus:ring-[#C8922A]/30 disabled:opacity-60"
              />
              {fieldErrors?.title && <p className="text-xs text-red-600 mt-1">{fieldErrors.title[0]}</p>}
            </div>

            <div>
              <label htmlFor="review-comment" className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] mb-2 block">
                Your review
              </label>
              <textarea
                id="review-comment"
                rows={5}
                value={comment}
                maxLength={COMMENT_MAX}
                disabled={submitting}
                onChange={(e) => setComment(e.target.value)}
                placeholder={`Tell others what the session was like (at least ${COMMENT_MIN} characters)`}
                className="w-full rounded-lg border border-[#D7C29A]/40 px-3.5 py-2.5 text-sm text-[#1C140A] focus:outline-none focus:ring-2 focus:ring-[#C8922A]/30 disabled:opacity-60 resize-none"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-red-600">{commentTooShort ? `At least ${COMMENT_MIN} characters` : (fieldErrors?.comment?.[0] ?? '')}</p>
                <p className="text-[11px] text-[#8B7355]/70 shrink-0">{comment.length}/{COMMENT_MAX}</p>
              </div>
            </div>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs text-red-600">{formError}</div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] border border-[#D7C29A]/50 text-[#8B7355] hover:bg-stone-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="hero-btn-primary flex-1 inline-flex items-center justify-center rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Submit review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
